import { useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { accountByCode } from '@abastece/core';
import { formatMoney, type Centavos } from '@abastece/shared';
import {
  cargarDetalle,
  cuandoLargo,
  describirRegistro,
  duracion,
  instante,
  resumen,
  type Fila,
  type RegistroAbierto,
} from '../lib/detalleEvento.js';
import { usePuede } from '../lib/sesion.js';
import { db } from '../db/dexie.js';
import { useT } from '../i18n/index.js';
import { etiquetaCampo } from '../i18n/campos.js';
import { etiquetaCuenta, etiquetaEventoIdioma, etiquetaRol } from '../i18n/dominio.js';
import { VerFotoCedula } from './FotoCedula.js';
import { CorregirConteo } from './CorregirConteo.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** A record that reached the server this much after it was made was made with no signal. */
const SIN_SENAL_MS = 10 * 60_000;
/** A device clock this far off the server's is worth saying, beside a time it wrote. */
const RELOJ_MS = 2 * 60_000;

/**
 * One record of «Bitácora», opened (the owner, 2026-10-12: «on the activity log for each item can
 * we have it be clickable and have it show all relevant details of that particular log event?»):
 * who did it, when, on which device, and whether the server has it; everything it recorded, said
 * the way a person reads it (`lib/detalleEvento.ts`); what came after it about the same thing; and,
 * for whoever sees costs, the entry the books made of it.
 *
 * A record it names — the sale a void is of, the till a sale was rung at, the product a price is
 * for — opens in this same sheet, and «Volver» goes back to the one before: the way it came.
 */
export function DetalleEvento({ eventoId, onCerrar }: { eventoId: string; onCerrar: () => void }) {
  const t = useT();
  const verCostos = usePuede('ver_costos');
  // A till's opening or corte counted wrong, put right by the owner from its record (2026-10-14).
  const puedeCorregir = usePuede('configurar');
  const [corrigiendo, setCorrigiendo] = useState<{ sesionId: string; cual: 'apertura' | 'corte' } | null>(null);
  const [pila, setPila] = useState<string[]>([eventoId]);
  const actual = pila[pila.length - 1]!;
  const cargado = useLiveQuery(() => cargarDetalle(actual), [actual]);
  // Refused by the books as the log was applied: not the count its till stood on.
  const sinEfecto = useLiveQuery(async () => Boolean(await db.sinEfecto.get(actual)), [actual], true);
  // While the next record loads the last one stays, so the sheet does not blink shut between them.
  const ultimo = useRef<RegistroAbierto | null | undefined>(undefined);
  if (cargado !== undefined) ultimo.current = cargado;
  const r = cargado ?? ultimo.current;
  if (r === undefined) return null;
  if (r === null) {
    return (
      <Modal titulo={t('bitacora.titulo')} onCerrar={onCerrar}>
        <p className="text-sm text-slate-400">{t('registro.noEstaAqui')}</p>
      </Modal>
    );
  }

  // The correction in the record's place; closed, the record comes back as it now stands.
  if (corrigiendo) return <CorregirConteo {...corrigiendo} onCerrar={() => setCorrigiendo(null)} />;

  const { evento: e, datos } = r;
  const { filas, bloques } = describirRegistro(e, datos);
  const sesionDelConteo = (e.payload as { sesionId?: unknown } | null)?.sesionId;
  const corregible =
    puedeCorregir &&
    (e.type === 'caja.abierta' || e.type === 'caja.cerrada') &&
    e.estado !== 'rechazado' &&
    !sinEfecto &&
    typeof sesionDelConteo === 'string';
  const quien = datos.usuarios.get(e.actorId);
  const equipo = datos.equipos.get(e.deviceId);
  // At the door, the record is in the name of whoever's PIN was tried: nobody had got in yet.
  const donde = (e.payload as { donde?: string } | null)?.donde;
  const enLaPuerta = e.type === 'sesion.pin_incorrecto' && (donde === 'entrada' || donde === 'desbloqueo');
  const tarde = e.serverTs ? Date.parse(e.serverTs) - Date.parse(e.clientTs) : 0;
  const desfase = e.clockOffsetMs ?? 0;
  // Late by more than the clock was off: it waited on the device, not on a wrong clock.
  const sinSenal = tarde > SIN_SENAL_MS && Math.abs(desfase) < tarde / 2;
  const relojMal = Math.abs(desfase) > RELOJ_MS;
  const abrir = (id: string) => setPila((p) => [...p, id]);
  const lineasDeLibro = datos.asientos.flatMap((a) => a.lineas);

  return (
    <Modal titulo={etiquetaEventoIdioma(e.type, e.payload)} onCerrar={onCerrar}>
      <div className="flex flex-col gap-5" data-registro={e.id} data-registro-tipo={e.type}>
        {pila.length > 1 && (
          <button
            type="button"
            className="btn-texto -mt-1 gap-1.5 self-start text-sm text-slate-400"
            onClick={() => setPila((p) => p.slice(0, -1))}
            data-registro-volver
          >
            <Ico icono="atras" className="size-4" />
            {t('comun.volver')}
          </button>
        )}

        <dl className="tarjeta flex flex-col gap-2 p-4" data-registro-quien>
          <Par
            etiqueta={t(enLaPuerta ? 'registro.seIntentoComo' : 'registro.loHizo')}
            valor={quien ? `${quien.nombre} · ${etiquetaRol(quien.rol)}` : t('bitacora.desconocido')}
          />
          <Par etiqueta={t('registro.cuando')} valor={cuandoLargo(e.clientTs)} />
          <Par etiqueta={t('registro.equipo')} valor={equipo ? `${equipo.nombre} (${equipo.codigo})` : t('registro.noEstaAqui')} />
          {e.serverSeq !== undefined && <Par etiqueta={t('registro.numero')} valor={`#${e.serverSeq}`} />}
          {e.estado === 'pendiente' && <p className="text-xs text-amber-300">{t('registro.soloAqui')}</p>}
          {e.estado === 'rechazado' && (
            <p className="text-xs text-rose-300">{t('registro.rechazado', { motivo: e.motivoRechazo ?? '' })}</p>
          )}
          {sinSenal && <p className="text-xs text-slate-400">{t('registro.llegoDespues', { cuanto: duracion(tarde) })}</p>}
          {relojMal && <p className="text-xs text-amber-300">{t('registro.reloj', { cuanto: duracion(desfase) })}</p>}
        </dl>

        <section>
          <h3 className="rotulo">{t('registro.loQueSeRegistro')}</h3>
          {filas.length === 0 && bloques.length === 0 ? (
            <p className="text-sm text-slate-500">{t('registro.nadaMas')}</p>
          ) : (
            <dl className="flex flex-col gap-2.5" data-registro-filas>
              {filas.map((f) => (
                <FilaDelRegistro key={f.campo} fila={f} onAbrir={abrir} />
              ))}
            </dl>
          )}
        </section>

        {corregible && (
          <button
            type="button"
            className="btn-secundario self-start"
            data-registro-corregir-conteo
            onClick={() => setCorrigiendo({ sesionId: sesionDelConteo, cual: e.type === 'caja.abierta' ? 'apertura' : 'corte' })}
          >
            <Ico icono="editar" />
            {t('conteoCaja.boton')}
          </button>
        )}

        {bloques.map((b) => (
          <section key={b.campo} data-registro-bloque={b.campo}>
            <h3 className="rotulo">{b.etiqueta}</h3>
            <ul className="flex flex-col divide-y divide-slate-800">
              {b.lineas.map((l, i) => (
                <li key={i} className="flex items-start gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm wrap-break-word">{l.titulo}</p>
                    {l.cantidad && <p className="tabular mt-0.5 text-xs text-slate-500">{l.cantidad}</p>}
                    {l.detalles.map((d, j) => (
                      <p key={j} className="mt-0.5 text-xs wrap-break-word text-slate-400">
                        {d}
                      </p>
                    ))}
                  </div>
                  {l.cifra && <span className="tabular shrink-0 text-sm">{l.cifra}</span>}
                </li>
              ))}
            </ul>
          </section>
        ))}

        {datos.despues.length > 0 && (
          <section data-registro-despues>
            <h3 className="rotulo">{t('registro.despues')}</h3>
            <ul className="flex flex-col gap-1.5">
              {datos.despues.map((x) => (
                <li key={x.id}>
                  <button
                    type="button"
                    onClick={() => abrir(x.id)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ring-1 ring-white/5 ring-inset transition hover:bg-slate-800 hover:ring-white/10"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{etiquetaEventoIdioma(x.type, x.payload)}</span>
                      <span className="mt-0.5 block text-xs wrap-break-word text-slate-500">
                        {[instante(x.clientTs), datos.usuarios.get(x.actorId)?.nombre, resumen(x, datos)].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* What it moved on the books: the cost of what sold among it, so only for whoever sees costs. */}
        {verCostos && lineasDeLibro.length > 0 && (
          <details className="group" data-registro-libros>
            <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-semibold tracking-wider text-slate-500 uppercase transition hover:text-slate-300 [&::-webkit-details-marker]:hidden">
              <Ico icono="desplegar" className="size-4 transition group-open:rotate-180" />
              {t('registro.libros')}
            </summary>
            <table className="mt-3 w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500">
                  <th className="pb-1 text-left font-normal">{etiquetaCampo('cuenta')}</th>
                  <th className="pb-1 pl-3 text-right font-normal">{t('registro.debe')}</th>
                  <th className="pb-1 pl-3 text-right font-normal">{t('registro.haber')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {lineasDeLibro.map((l, i) => (
                  <tr key={i}>
                    <td className="py-1.5 text-slate-300">
                      <span className="tabular text-slate-500">{l.cuenta}</span>{' '}
                      {etiquetaCuenta(l.cuenta, accountByCode(l.cuenta)?.name ?? l.cuenta, datos.config.cuentasBanco)}
                    </td>
                    <td className="tabular py-1.5 pl-3 text-right whitespace-nowrap">
                      {l.debe ? formatMoney(l.debe as Centavos, { symbol: false }) : ''}
                    </td>
                    <td className="tabular py-1.5 pl-3 text-right whitespace-nowrap">
                      {l.haber ? formatMoney(l.haber as Centavos, { symbol: false }) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>
    </Modal>
  );
}

function Par({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-sm text-slate-400">{etiqueta}</dt>
      <dd className="min-w-0 text-right text-sm wrap-break-word">{valor}</dd>
    </div>
  );
}

/**
 * One thing the record holds. A figure beside its name; words under it, wrapping; a photo drawn;
 * another record as a row that opens it, with what it is, when and by whom; and, for a change,
 * what it said before.
 */
function FilaDelRegistro({ fila: f, onAbrir }: { fila: Fila; onAbrir: (id: string) => void }) {
  const t = useT();
  const debajo = (
    <>
      {f.nota && <span className="mt-0.5 block text-xs font-normal text-slate-500">{f.nota}</span>}
      {f.antes !== undefined && (
        <span className="mt-0.5 block text-xs font-normal text-slate-500" data-registro-antes>
          {t('registro.antes', { valor: f.antes })}
        </span>
      )}
    </>
  );

  if (f.foto) {
    return (
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm text-slate-400">{f.etiqueta}</dt>
        <dd>
          <VerFotoCedula huella={f.foto} titulo={f.etiqueta} />
        </dd>
      </div>
    );
  }
  if (f.abre) {
    const abre = f.abre;
    return (
      <div className="flex flex-col gap-1">
        <dt className="text-sm text-slate-400">{f.etiqueta}</dt>
        <dd>
          <button
            type="button"
            onClick={() => onAbrir(abre)}
            data-registro-abre={f.campo}
            className="flex w-full items-center gap-3 rounded-xl bg-slate-800/40 px-3 py-2 text-left text-sm ring-1 ring-white/5 ring-inset transition hover:bg-slate-800 hover:ring-white/10"
          >
            <span className="min-w-0 flex-1">
              <span className="block font-medium wrap-break-word">{f.valor}</span>
              {debajo}
            </span>
            <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
          </button>
        </dd>
      </div>
    );
  }
  if (f.largo) {
    return (
      <div className="flex flex-col gap-0.5">
        <dt className="text-sm text-slate-400">{f.etiqueta}</dt>
        <dd className="text-sm font-medium wrap-break-word">
          {f.valor}
          {debajo}
        </dd>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="min-w-0 text-sm text-slate-400">{f.etiqueta}</dt>
      <dd className="tabular min-w-0 text-right text-sm font-medium wrap-break-word">
        {f.valor}
        {debajo}
      </dd>
    </div>
  );
}
